// Archivo de entrada para crear bundle del widget React
import { createRoot } from 'react-dom/client'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import InfoCardWidget from './widgets/InfoCardWidget'
import type { InfoCardWidgetProps } from './widgets/InfoCardWidget'
import FavioWidget from './widgets/FavioWidget'
import type { FavioWidgetProps } from './widgets/FavioWidget'
import GenericWidget from './widgets/GenericWidget'
import type { GenericWidgetProps } from './widgets/GenericWidget'
import FeaturedPropertiesCarousel from './widgets/FeaturedPropertiesCarousel'
import type { FeaturedPropertiesCarouselProps } from './widgets/FeaturedPropertiesCarousel'
import FeaturedAgentsDetail from './widgets/FeaturedAgentsDetail'
import type { FeaturedAgentsDetailProps } from './widgets/FeaturedAgentsDetail'
import GetInTouchWidget from './widgets/getInTouch/GetInTouchWidget'
import type { GetInTouchWidgetProps } from './widgets/getInTouch/GetInTouchWidget'
import { AuthProvider } from './contexts/AuthContext'

// Configuración de React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutos
      gcTime: 10 * 60 * 1000, // 10 minutos
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// Tema por defecto
const defaultTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
})

// Función para renderizar el widget en un contenedor
function renderInfoCardWidget(containerId: string, props: InfoCardWidgetProps) {
  const container = document.getElementById(containerId)
  if (!container) {
    console.error(`Container with id "${containerId}" not found`)
    return
  }

  const root = createRoot(container)
  root.render(
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider theme={defaultTheme}>
          <CssBaseline />
          <InfoCardWidget {...props} />
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  )
}

// Función para crear múltiples widgets
function renderMultipleWidgets(containers: Array<{ id: string; props: InfoCardWidgetProps }>) {
  containers.forEach(({ id, props }) => {
    renderInfoCardWidget(id, props)
  })
}

// Función para crear widget con tema personalizado
function renderInfoCardWidgetWithTheme(
  containerId: string, 
  props: InfoCardWidgetProps, 
  customTheme?: any
) {
  const container = document.getElementById(containerId)
  if (!container) {
    console.error(`Container with id "${containerId}" not found`)
    return
  }

  const root = createRoot(container)
  const theme = customTheme ? createTheme(customTheme) : defaultTheme
  
  root.render(
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <InfoCardWidget {...props} />
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  )
}

// Función para renderizar el FavioWidget
function renderFavioWidget(containerId: string, props: FavioWidgetProps) {
  const container = document.getElementById(containerId)
  if (!container) {
    console.error(`Container with id "${containerId}" not found`)
    return
  }

  const root = createRoot(container)
  root.render(
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider theme={defaultTheme}>
          <CssBaseline />
          <FavioWidget {...props} />
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  )
}

// Función para crear múltiples FavioWidgets
function renderMultipleFavioWidgets(containers: Array<{ id: string; props: FavioWidgetProps }>) {
  containers.forEach(({ id, props }) => {
    renderFavioWidget(id, props)
  })
}

// Función para renderizar el GenericWidget
function renderGenericWidget(containerId: string, props: GenericWidgetProps) {
  const container = document.getElementById(containerId)
  if (!container) {
    console.error(`Container with id "${containerId}" not found`)
    return
  }

  // Mostrar loading inmediatamente mientras React se inicializa
  container.innerHTML = '<div style="padding: 20px; text-align: center; color: #666;">Loading...</div>'

  // Usar requestAnimationFrame para asegurar que el DOM se actualice antes de renderizar React
  requestAnimationFrame(() => {
    const root = createRoot(container)
    root.render(
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemeProvider theme={defaultTheme}>
            <CssBaseline />
            <GenericWidget {...props} />
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    )
  })
}

// Función para crear múltiples GenericWidgets
function renderMultipleGenericWidgets(containers: Array<{ id: string; props: GenericWidgetProps }>) {
  containers.forEach(({ id, props }) => {
    renderGenericWidget(id, props)
  })
}

// Función para renderizar el FeaturedPropertiesCarousel
function renderFeaturedPropertiesCarousel(containerId: string, props: FeaturedPropertiesCarouselProps) {
  const container = document.getElementById(containerId)
  if (!container) {
    console.error(`Container with id "${containerId}" not found`)
    return
  }

  const root = createRoot(container)
  root.render(
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider theme={defaultTheme}>
          <CssBaseline />
          <FeaturedPropertiesCarousel {...props} />
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  )
}

// Función para crear múltiples FeaturedPropertiesCarousel
function renderMultipleFeaturedPropertiesCarousel(containers: Array<{ id: string; props: FeaturedPropertiesCarouselProps }>) {
  containers.forEach(({ id, props }) => {
    renderFeaturedPropertiesCarousel(id, props)
  })
}

// Función para renderizar el FeaturedAgentsDetail
function renderFeaturedAgentsDetail(containerId: string, props: FeaturedAgentsDetailProps) {
  const container = document.getElementById(containerId)
  if (!container) {
    console.error(`Container with id "${containerId}" not found`)
    return
  }

  const root = createRoot(container)
  root.render(
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider theme={defaultTheme}>
          <CssBaseline />
          <FeaturedAgentsDetail {...props} />
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  )
}

// Función para renderizar el GetInTouchWidget
function renderGetInTouchWidget(containerId: string, props: GetInTouchWidgetProps) {
  const container = document.getElementById(containerId)
  if (!container) {
    console.error(`Container with id "${containerId}" not found`)
    return
  }

  const root = createRoot(container)
  root.render(
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider theme={defaultTheme}>
          <CssBaseline />
          <GetInTouchWidget {...props} />
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  )
}

// Función para crear múltiples GetInTouchWidgets
function renderMultipleGetInTouchWidgets(containers: Array<{ id: string; props: GetInTouchWidgetProps }>) {
  containers.forEach(({ id, props }) => {
    renderGetInTouchWidget(id, props)
  })
}

// Exportar funciones globalmente
if (typeof window !== 'undefined') {
  (window as any).WidgetForge = {
    renderInfoCardWidget,
    renderMultipleWidgets,
    renderInfoCardWidgetWithTheme,
    renderFavioWidget,
    renderMultipleFavioWidgets,
    renderGenericWidget,
    renderMultipleGenericWidgets,
    renderFeaturedPropertiesCarousel,
    renderMultipleFeaturedPropertiesCarousel,
    renderFeaturedAgentsDetail,
    renderGetInTouchWidget,
    renderMultipleGetInTouchWidgets,
    InfoCardWidget,
    FavioWidget,
    GenericWidget,
    FeaturedPropertiesCarousel,
    FeaturedAgentsDetail,
    GetInTouchWidget,
    createTheme
  }
}

// Exportar para módulos
export {
  renderInfoCardWidget,
  renderMultipleWidgets,
  renderInfoCardWidgetWithTheme,
  renderFavioWidget,
  renderMultipleFavioWidgets,
  renderGenericWidget,
  renderMultipleGenericWidgets,
  renderFeaturedPropertiesCarousel,
  renderMultipleFeaturedPropertiesCarousel,
  renderFeaturedAgentsDetail,
  renderGetInTouchWidget,
  renderMultipleGetInTouchWidgets,
  InfoCardWidget,
  FavioWidget,
  GenericWidget,
  FeaturedPropertiesCarousel,
  FeaturedAgentsDetail,
  GetInTouchWidget
}

export type {
  InfoCardWidgetProps,
  FavioWidgetProps,
  GenericWidgetProps,
  FeaturedPropertiesCarouselProps,
  FeaturedAgentsDetailProps,
  GetInTouchWidgetProps
}
